import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface BarChartProps {
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      borderWidth?: number;
    }[];
  };
  title?: string;
  height?: number;
  horizontal?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({ 
  data, 
  title, 
  height = 400, 
  horizontal = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  
  const options: ChartOptions<'bar'> = {
    indexAxis: horizontal ? 'y' as const : 'x' as const,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: isSmallMobile ? 9 : isMobile ? 11 : isLargeScreen ? 16 : 14,
            weight: 600,
            family: 'Roboto, Arial, sans-serif',
          },
          padding: isSmallMobile ? 8 : isMobile ? 12 : isLargeScreen ? 24 : 20,
          usePointStyle: true,
          color: '#1B5E20',
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: isSmallMobile ? 14 : isMobile ? 16 : isLargeScreen ? 20 : 18,
          weight: 'bold',
        },
        padding: {
          bottom: isMobile ? 15 : isLargeScreen ? 30 : 25,
        },
        color: '#26372D',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || context.parsed.x;
            return `${label}: ${value.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: isSmallMobile ? 8 : isMobile ? 10 : isLargeScreen ? 14 : 12,
            weight: 500,
            family: 'Roboto, Arial, sans-serif',
          },
          color: '#1B5E20',
          callback: function(value) {
            if (typeof value === 'number') {
              return value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            }
            return value;
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: isSmallMobile ? 8 : isMobile ? 10 : isLargeScreen ? 14 : 12,
            weight: 500,
            family: 'Roboto, Arial, sans-serif',
          },
          color: '#1B5E20',
          callback: function(value, index, values) {
            if (typeof value === 'number') {
              return value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            }
            // Truncar labels longos em mobile
            if (typeof value === 'string') {
              if (isSmallMobile && value.length > 10) {
                return value.substring(0, 8) + '...';
              } else if (isMobile && value.length > 15) {
                return value.substring(0, 12) + '...';
              }
            }
            return value;
          }
        }
      }
    },
  };

  return (
    <div style={{ 
      height: `${height}px`, 
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Bar data={data} options={options} />
    </div>
  );
};